export type SegmentStatus = "PENDING" | "ACTIVE" | "INACTIVE";

export interface Vendor {
  id: number;
  name: string;
}

/** A catalog row. `storedActive` is the boolean Select stores; the label buyers see is derived. */
export interface Segment {
  id: number;
  name: string;
  description: string;
  /** The id the vendor assigned. Campaigns store this, not the catalog row id. */
  segmentId: number;
  akDspIpListId: number | null;
  storedActive: boolean;
  vendorId: number;
  /** Campaigns and line items whose audience list contains this vendor segment id. */
  targetingReferences: number;
}

export interface SegmentInput {
  name: string;
  description: string;
  segmentId: number | null;
  active: boolean;
}

export type SegmentField = "name" | "description" | "segmentId";

export interface FieldError {
  field: SegmentField;
  message: string;
}

export const vendors: Vendor[] = [
  { id: 1, name: "Lotame" },
  { id: 2, name: "Experian" },
  { id: 3, name: "LiveRamp" },
];

export const segments: Segment[] = [
  {
    id: 1,
    name: "Quit-smoking intenders",
    description: "People researching how to quit smoking, United States.",
    segmentId: 1106480,
    akDspIpListId: 482110,
    storedActive: true,
    vendorId: 1,
    targetingReferences: 2,
  },
  {
    id: 2,
    name: "Women 35-54",
    description: "Women aged 35 to 54.",
    segmentId: 1130564,
    akDspIpListId: 482164,
    storedActive: true,
    vendorId: 1,
    targetingReferences: 0,
  },
  {
    id: 3,
    name: "Auto intenders",
    description: "In-market for a vehicle in the next 90 days.",
    segmentId: 1116528,
    akDspIpListId: null,
    storedActive: false,
    vendorId: 1,
    targetingReferences: 1,
  },
  {
    id: 4,
    name: "Travel planners",
    description: "Households planning leisure travel.",
    segmentId: 1099001,
    akDspIpListId: 470021,
    storedActive: false,
    vendorId: 1,
    targetingReferences: 0,
  },
  {
    id: 5,
    name: "In-market homeowners",
    description: "Homeowners likely to move or refinance.",
    segmentId: 1088120,
    akDspIpListId: 469880,
    storedActive: true,
    vendorId: 1,
    targetingReferences: 0,
  },
  {
    id: 6,
    name: "Affluent households",
    description: "Household income in the top quintile.",
    segmentId: 2200144,
    akDspIpListId: null,
    storedActive: false,
    vendorId: 2,
    targetingReferences: 0,
  },
  {
    id: 7,
    name: "Grocery weekly shoppers",
    description: "Households that buy groceries every week.",
    segmentId: 2204488,
    akDspIpListId: 510332,
    storedActive: true,
    vendorId: 2,
    targetingReferences: 4,
  },
];

export function segmentStatus(segment: Segment): SegmentStatus {
  if (segment.akDspIpListId == null) return "PENDING";
  return segment.storedActive ? "ACTIVE" : "INACTIVE";
}

export function statusLabel(status: SegmentStatus): string {
  if (status === "ACTIVE") return "Active";
  if (status === "PENDING") return "Pending";
  return "Inactive";
}

export function statusExplanation(segment: Segment): string {
  const status = segmentStatus(segment);
  if (status === "PENDING") {
    return "Approved for ingestion, but no AdKernel IP list is bound yet. Buyers cannot target it.";
  }
  if (status === "ACTIVE") {
    return "Targetable. Buyers can attach it to a campaign or line item, and the daily job refreshes its IP list.";
  }
  return "Turned off. Buyers cannot newly target it, and the daily refresh skips it. The IP list stays in AdKernel.";
}

export function ipListName(segment: Pick<Segment, "segmentId" | "name">): string {
  const slug = segment.name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${segment.segmentId}_${slug || "segment"}`;
}

export function validateVendorName(
  name: string,
  vendorList: Vendor[],
  ignoreId?: number,
): string | null {
  const trimmed = name.trim();
  if (!trimmed) return "Enter a vendor name.";
  if (trimmed.length > 200) return "Name must be 200 characters or fewer.";
  const taken = vendorList.some(
    (vendor) => vendor.id !== ignoreId && vendor.name === trimmed,
  );
  if (taken) return "A vendor with this name already exists.";
  return null;
}

export function validateSegment(
  input: SegmentInput,
  vendorId: number,
  segmentList: Segment[],
  existing?: Segment,
): FieldError | null {
  const name = input.name.trim();
  if (!name) return { field: "name", message: "Enter a segment name." };
  if (name.length > 80) {
    return { field: "name", message: "Name must be 80 characters or fewer." };
  }
  const nameTaken = segmentList.some(
    (segment) => segment.id !== existing?.id && segment.name === name,
  );
  if (nameTaken) {
    return { field: "name", message: "A segment with this name already exists." };
  }

  if (input.description.length > 200) {
    return {
      field: "description",
      message: "Description must be 200 characters or fewer.",
    };
  }

  if (input.segmentId == null || !Number.isInteger(input.segmentId) || input.segmentId < 1) {
    return {
      field: "segmentId",
      message: "Enter the id the vendor assigned to this audience.",
    };
  }
  const idTaken = segmentList.some(
    (segment) =>
      segment.id !== existing?.id &&
      segment.vendorId === vendorId &&
      segment.segmentId === input.segmentId,
  );
  if (idTaken) {
    return {
      field: "segmentId",
      message: "This vendor already has a segment with that id.",
    };
  }

  return null;
}

export function deleteSegmentBlockReason(segment: Segment): string | null {
  if (segment.storedActive) {
    return "Active segments cannot be deleted. Set this segment to Inactive first.";
  }
  if (segment.targetingReferences > 0) {
    return "This segment is still on a campaign or line item.";
  }
  return null;
}

export function deleteVendorBlockReason(
  vendorId: number,
  segmentList: Segment[],
): string | null {
  const count = segmentList.filter((segment) => segment.vendorId === vendorId).length;
  if (count === 0) return null;
  if (count === 1) return "Delete the remaining segment before deleting this vendor.";
  return `Delete this vendor's ${count} segments first.`;
}

export function countByStatus(segmentList: Segment[]) {
  const counts = { total: segmentList.length, ACTIVE: 0, PENDING: 0, INACTIVE: 0 };
  for (const segment of segmentList) {
    counts[segmentStatus(segment)] += 1;
  }
  return counts;
}
